import React from 'react';
import { Dialog } from '../../ui';
import { ProviderResource } from '../../../hooks/utils/useProviderResources';
import { ProviderName } from '../../../hooks/api/useExternalProviderPlans';
import { useResourceModalData, Resource } from './useResourceModalData';
import { useResourceModalAnalytics } from './useResourceModalAnalytics';
import { ResourceModalHeader } from './resource-modal-header';
import { ResourceModalContent } from './resource-modal-content';
import { ResourceModalCtaButtons } from './resource-modal-cta-buttons';
import { ResourceModalStyles } from './resource-modal-styles';

export interface ResourceModalProps {
  resource?: Resource;
  onClose: () => void;
  providerName?: ProviderName;
  providerPath?: string;
  providerResource?: ProviderResource;
}

export const ResourceModal: React.FC<ResourceModalProps> = ({
  resource: resourceProp,
  onClose,
  providerName,
  providerResource,
}) => {
  const modalData = useResourceModalData({
    resource: resourceProp,
    providerResource,
    providerName,
  });

  const {
    resource,
    isProviderResource,
    contentHtml,
    partnerResourceConfig,
    ssoLinkLoading,
    url,
    finalDetectedProviderName,
    urlError,
    errorMessage,
    displayPhoneNumber,
    isGlobal,
  } = modalData;

  const { handleHubspotCallButton, handleHubspotLinkButton } = useResourceModalAnalytics({
    resource,
    isProviderResource,
    isGlobal,
    finalDetectedProviderName,
    errorMessage,
    urlError,
  });

  if (!resource) {
    return null;
  }

  return (
    <>
      <ResourceModalStyles />
      <Dialog
        className="resource-modal-mobile backdrop:bg-modalBackdropColor rounded-b-0 rounded-t-3 border-input-border-gray w-full max-w-[846px] border-l border-r border-t bg-white md:m-auto md:max-h-[90vh] md:rounded-xl md:border"
        open={true}
        onClose={onClose}
        contentClassName="flex flex-col h-full overflow-hidden md:h-auto md:max-h-[90vh] md:m-auto"
      >
        <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
          <ResourceModalHeader onClose={onClose} />

          {/* Body - Scrollable on mobile */}
          <div
            className="resource-modal-scroll min-h-0 flex-1 overflow-y-auto pb-[84px] md:pb-0"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#d9d9d9 transparent' }}
          >
            {!isProviderResource && <div className="mb-6" />}
            <ResourceModalContent
              resource={resource}
              isProviderResource={isProviderResource}
              contentHtml={contentHtml}
              resourceProp={resourceProp}
            />
            {!isProviderResource && <div className="mb-6" />}
          </div>

          {/* Footer: CTA Buttons - Fixed on mobile */}
          <ResourceModalCtaButtons
            resource={resource}
            isProviderResource={isProviderResource}
            isGlobal={isGlobal}
            finalDetectedProviderName={finalDetectedProviderName}
            partnerResourceConfig={partnerResourceConfig}
            ssoLinkLoading={ssoLinkLoading}
            url={url}
            errorMessage={errorMessage}
            urlError={urlError}
            displayPhoneNumber={displayPhoneNumber}
            onCallClick={handleHubspotCallButton}
            onLinkClick={handleHubspotLinkButton}
          />
        </div>
      </Dialog>
    </>
  );
};
